import { Notification, NotificationOptions, EVENT_NAME } from '../types/Notifications.types.js';
import NotificationEvent from '@adobe-fonts/site/events/NotificationEvent.js';

export const TYPES = {
  ERROR: 'negative',
  NEUTRAL: '',
  NOTICE: 'info',
  SUCCESS: 'positive',
  WARN: 'warn'
};

const MAX_NUMBER_OF_MESSAGES = 3;
const DEFAULT_TIMEOUT = 6000;
const MIN_CHARACTERS_FOR_TIMEOUT = 120;
const INCREMENTAL_TIMEOUT = 1000;

/**
 * For accessibility, we need to add additional timeout to messages with more than 120 characters
 * https://opensource.adobe.com/spectrum-web-components/components/toast/#allow-time-for-reading
 *
 * @param text
 * @returns {number}
 */
export const calculateNotificationTimeout = (text: string) => {
  const messageLength = text.length;
  const additionalTimeout = messageLength > MIN_CHARACTERS_FOR_TIMEOUT ? INCREMENTAL_TIMEOUT * Math.floor((messageLength - MIN_CHARACTERS_FOR_TIMEOUT) / MIN_CHARACTERS_FOR_TIMEOUT) : 0;
  const defaultTimeout = DEFAULT_TIMEOUT + additionalTimeout;
  return defaultTimeout;
};

export class NotificationService {
  _messages: Notification[];
  _queue: Notification[];
  _nextNotificationId: number;

  constructor() {
    this._messages = [];
    this._queue = [];
    this._nextNotificationId = 0;

    this.handleEvents();
  }

  /**
   * Handle NotificationEvents
   * @private
   */
  private handleEvents = () => {
    if (typeof window === 'undefined') {
      return;
    }

    window.addEventListener(NotificationEvent.EVENT_NAME, this._handleNotificationEvent);
  };

  /**
   * Handle incoming NotificationEvents
   * @private
   */
  private _handleNotificationEvent = (event: Event) => {
    const customEvent = event as CustomEvent;
    const { type, message, options = {} } = customEvent.detail;

    // Map of valid notification types to their handler methods
    const handlers = {
      success: this.success,
      error: this.error,
      notice: this.notice,
      warn: this.warn,
      neutral: this.neutral
    };

    const handler = handlers[type as keyof typeof handlers];
    if (handler) {
      handler(message, options);
    } else {
      console.warn(`Unknown notification type: ${type}`);
    }
  };

  /**
   * Dispatch a custom event to notify about message changes
   * @private
   */
  private _dispatchChangeEvent = () => {
    const messages = this.getMessages();
    const event = new CustomEvent(EVENT_NAME, {
      detail: {
        messages: messages,
        hasMessages: messages.length > 0
      },
      bubbles: true
    });

    window.dispatchEvent(event);
  };

  /**
   * Returns all messages or just messages of the type passed in.
   *
   * @param {?String} type
   * @returns {[{}]}
   */
  getMessages = (type = '') => {
    const visibleMessages = this._messages.slice(0, MAX_NUMBER_OF_MESSAGES);
    if (type) {
      return visibleMessages.filter((message) => {
        return message.type == type;
      });
    }

    return visibleMessages;
  };

  /**
   * Add a message to the service.
   * If at capacity, queues the message for later display.
   *
   * @param {String} text
   * @param {?String} type
   * @param {?Object} options
   */
  add = (text: string, type = '', options: NotificationOptions = {}) => {
    // Check for duplicates in both active messages and queue
    const isDuplicate = this._messages.some(m => m.text === text) ||
                       this._queue.some(m => m.text === text);

    if (!isDuplicate) {
      if (!options.timeout) {
        options.timeout = calculateNotificationTimeout(text);
      }

      const message = this._createMessage(text, type, options);

      if (this._messages.length >= MAX_NUMBER_OF_MESSAGES) {
        this._queue.push(message);
      } else {
        this._addToActive(message);
      }
    }
  };

  /**
   * Add a message to active display and setup auto-removal
   * @private
   */
  private _addToActive = (message: Notification) => {
    this._messages.push(message);
    this._dispatchChangeEvent();

    if (!message.skipTimeout && message.timeout) {
      setTimeout(() => this.remove(message.id), message.timeout);
    }
  };

  /**
   * Removes a message from the list of messages.
   * Automatically promotes the next queued message if available.
   *
   * @param {{}} messageToRemove
   */
  remove = (messageId: number) => {
    const originalLength = this._messages.length;
    this._messages = this._messages.filter((message) => {
      return message.id !== messageId;
    });

    if (this._messages.length !== originalLength) {
      // Message was removed, check if we can promote from queue
      this._promoteFromQueue();
      this._dispatchChangeEvent();
    }
  };

  /**
   * Promote the next message from queue to active display
   * @private
   */
  private _promoteFromQueue = () => {
    if (this._queue.length > 0 && this._messages.length < MAX_NUMBER_OF_MESSAGES) {
      const nextMessage = this._queue.shift();
      if (nextMessage) {
        this._addToActive(nextMessage);
      }
    }
  };

  /**
   * Removes all messages from the list of messages and clears the queue.
   */
  removeAll = () => {
    if (this._messages.length > 0 || this._queue.length > 0) {
      this._messages = [];
      this._queue = [];
      this._dispatchChangeEvent();
    }
  };

  /**
   * Get the number of messages currently queued
   * @returns {number}
   */
  getQueuedCount = (): number => {
    return this._queue.length;
  };

  /**
   * Get queue information for debugging
   * @returns {Object}
   */
  getQueueInfo = () => {
    return {
      active: this._messages.length,
      queued: this._queue.length,
      total: this._messages.length + this._queue.length,
      capacity: MAX_NUMBER_OF_MESSAGES
    };
  };

  /**
   * Adds an error message to the service.
   * @param {String} text
   * @param {?Object} options
   */
  error = (text: string, options: NotificationOptions = {}) => {
    this.add(text, TYPES.ERROR, options);
  };

  /**
   * Adds a neutral message to the service.
   * @param {String} text
   * @param {?Object} options
   */
  neutral = (text: string, options: NotificationOptions = {}) => {
    this.add(text, TYPES.NEUTRAL, options);
  };

  /**
   * Adds a notice message to the service.
   * @param {String} text
   * @param {?Object} options
   */
  notice = (text: string, options: NotificationOptions = {}) => {
    this.add(text, TYPES.NOTICE, options);
  };

  /**
   * Adds a success message to the service.
   * @param {String} text
   * @param {?Object} options
   */
  success = (text: string, options: NotificationOptions = {}) => {
    this.add(text, TYPES.SUCCESS, options);
  };

  /**
   * Adds a warning message to the service.
   * @param {String} text
   * @param {?Object} options
   */
  warn = (text: string, options: NotificationOptions = {}) => {
    this.add(text, TYPES.WARN, options);
  };

  /**
   * Displays the message as either a notice or error, depending on the displayError argument
   * @param {String} message
   * @param {Boolean} displayError
   */
  toggleErrorMessage = (message: string, displayError: boolean) => {
    return displayError ? this.error(message) : this.notice(message);
  };

  /**
   * Creates a message.
   *
   * @private
   * @param {String} text
   * @param {?String} type
   * @param {?Object} options
   * @returns {Notification}
   */
  private _createMessage = (text: string, type = '', options: NotificationOptions = {}): Notification => {
    const messageType = type || TYPES.NOTICE;

    let actionLink;
    let actionLabel;
    let callbackAction;

    if (options.actionLabel) {
      actionLabel = options.actionLabel;
    }

    if (options.actionLink && options.actionLabel) {
      actionLink = options.actionLink;
      actionLabel = options.actionLabel;
    }

    if (options.callbackAction && options.actionLabel) {
      actionLabel = options.actionLabel;
      callbackAction = options.callbackAction;
    }

    return {
      id: this._nextNotificationId++,
      text: text,
      type: messageType,
      ...(actionLink && { actionLink }),
      ...(actionLabel && { actionLabel }),
      ...(callbackAction && { callbackAction }),
      ...(options.category && { category: options.category }),
      ...(options.queryParams && { queryParams: options.queryParams }),
      ...(options.scriptIdentifier && { scriptIdentifier: options.scriptIdentifier }),
      ...(options.timeout && { timeout: options.timeout }),
      ...(options.tooltipText && { tooltipText: options.tooltipText })
    };
  };
}

let instance: NotificationService | null = null;

export const getNotificationService = (): NotificationService => {
  if (!instance) {
    instance = new NotificationService();
  }
  return instance;
};
